import { useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  CATALOG_ROOMS,
  FURNITURE_CATALOG,
  catalogItem,
  catalogPick as sharedCatalogPick,
  describeFurnitureSpec,
  FURNITURE_FINISHES,
  withFinish,
  type CatalogItem,
  type FurnitureFinish,
  type CatalogPick,
  type FurnitureSpec,
  type FurnitureTemplate,
} from '@ousse/shared'
import { api } from '../lib/api'
import { useHousehold } from '../lib/household'
import { ConfirmButton, ErrorAlert } from './ui'

export type { CatalogPick }

/**
 * Finition (couleur du meuble dans la vue 3D) : une pastille et son nom par finition, « Naturelle » garde la couleur
 * propre au dessin du meuble.
 */
export function FinishPicker({ value, onChange }: { value: FurnitureFinish; onChange: (finish: FurnitureFinish) => void }) {
  const { t } = useTranslation()
  const name = useId()
  return (
    <fieldset className="radio-group">
      <legend>{t('catalog.finish')}</legend>
      <div className="finish-list">
        {FURNITURE_FINISHES.map((f) => (
          <label key={f.id} className="finish">
            <input type="radio" name={name} value={f.id} checked={value === f.id} onChange={() => onChange(f.id)} />
            <span className={`finish-swatch${f.color ? '' : ' natural'}`} style={f.color ? { background: f.color } : undefined} aria-hidden />
            {t(`catalog.finishes.${f.id}`)}
          </label>
        ))}
      </div>
    </fieldset>
  )
}

/** « Mes modèles » du foyer (même cache partout). */
export function useFurnitureTemplates(householdId: string | undefined) {
  return useQuery({
    queryKey: ['furniture-templates', householdId],
    enabled: Boolean(householdId),
    queryFn: () => api.get<{ items: FurnitureTemplate[] }>(`/households/${householdId}/furniture-templates`).then((r) => r.items),
  })
}

/** « 80 × 45 cm, 85 cm de haut · avec 4 tiroirs ». */
export function describeSpec(t: TFunction, spec: FurnitureSpec, item?: CatalogItem): string {
  return describeFurnitureSpec((key, opts) => t(key, opts), spec, item)
}

/** Modèle du catalogue → choix (nom et noms des parties dans la langue de l'écran). */
export function catalogPick(t: TFunction, item: CatalogItem): CatalogPick {
  return sharedCatalogPick((key, opts) => t(key, opts), item)
}

/**
 * Catalogue en liste (vue 3D) : une rubrique par pièce, puis « Mes modèles » (retirer un modèle : confirmé).
 * Chaque meuble : nom, taille, intérieur.
 */
export function FurnitureCatalogPicker({
  householdId,
  canWrite,
  onPick,
}: {
  householdId: string
  canWrite: boolean
  onPick: (pick: CatalogPick) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const templates = useFurnitureTemplates(householdId)
  const [finish, setFinish] = useState<FurnitureFinish>('natural')
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/households/${householdId}/furniture-templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['furniture-templates', householdId] }),
  })
  const entry = (key: string, pick: CatalogPick, detail: string) => (
    <button
      type="button"
      key={key}
      className="btn ghost catalog-entry"
      onClick={() => onPick(finish === 'natural' ? pick : { ...pick, spec: withFinish(pick.spec, finish) })}
    >
      <span className="catalog-name">{pick.name}</span>
      <span className="muted catalog-detail">{detail}</span>
    </button>
  )
  return (
    <div className="stack" style={{ gap: '0.75rem' }}>
      <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{t('catalog.title')}</h2>
      <p className="muted" style={{ margin: 0 }}>
        {t('catalog.hint')}
      </p>
      <FinishPicker value={finish} onChange={setFinish} />
      <details open>
        <summary className="catalog-summary">{t('catalog.mine')}</summary>
        <div className="stack catalog-list">
          {templates.data?.length === 0 && <p className="muted">{t('catalog.mineEmpty')}</p>}
          {templates.data?.map((tpl) => (
            <div key={tpl.id} className="stack" style={{ gap: '0.25rem' }}>
              {entry(
                tpl.id,
                { name: tpl.name, spec: tpl.spec },
                tpl.scope === 'personal' ? `${describeSpec(t, tpl.spec)} · ${t('catalog.personalTemplate')}` : describeSpec(t, tpl.spec),
              )}
              {canWrite && (
                <ConfirmButton className="btn link" message={t('catalog.deleteTemplateConfirm', { name: tpl.name })} onConfirm={() => remove.mutate(tpl.id)}>
                  {t('catalog.deleteTemplate')}
                </ConfirmButton>
              )}
              {tpl.scope === 'household' && <CopyTemplate householdId={householdId} template={tpl} />}
            </div>
          ))}
          <ErrorAlert error={templates.error ?? remove.error} />
        </div>
      </details>
      {CATALOG_ROOMS.map((room) => (
        <details key={room}>
          <summary className="catalog-summary">{t(`catalog.rooms.${room}`)}</summary>
          <div className="stack catalog-list">
            {FURNITURE_CATALOG.filter((i) => i.room === room).map((item) => {
              const pick = catalogPick(t, item)
              return entry(item.id, pick, describeSpec(t, pick.spec, item))
            })}
          </div>
        </details>
      ))}
    </div>
  )
}

/**
 * Catalogue en liste déroulante (création d'un rangement) : « Sans modèle », une rubrique par pièce, « Mes
 * modèles ». La valeur est `catalog:<id>`, `template:<id>` ou vide.
 */
export function FurnitureModelSelect({
  householdId,
  value,
  onChange,
}: {
  householdId: string
  value: string
  onChange: (value: string, pick: CatalogPick | null) => void
}) {
  const { t } = useTranslation()
  const id = useId()
  const templates = useFurnitureTemplates(householdId)
  const resolve = (v: string): CatalogPick | null => {
    if (v.startsWith('catalog:')) {
      const item = catalogItem(v.slice('catalog:'.length))
      return item ? catalogPick(t, item) : null
    }
    if (v.startsWith('template:')) {
      const tpl = templates.data?.find((x) => x.id === v.slice('template:'.length))
      return tpl ? { name: tpl.name, spec: tpl.spec } : null
    }
    return null
  }
  const pick = resolve(value)
  const item = value.startsWith('catalog:') ? catalogItem(value.slice('catalog:'.length)) : undefined
  return (
    <div className="field">
      <label htmlFor={id}>{t('catalog.pick')}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value, resolve(e.target.value))} aria-describedby={`${id}-hint`}>
        <option value="">{t('catalog.none')}</option>
        {Boolean(templates.data?.length) && (
          <optgroup label={t('catalog.mine')}>
            {templates.data!.map((tpl) => (
              <option key={tpl.id} value={`template:${tpl.id}`}>
                {tpl.name}
              </option>
            ))}
          </optgroup>
        )}
        {CATALOG_ROOMS.map((room) => (
          <optgroup key={room} label={t(`catalog.rooms.${room}`)}>
            {FURNITURE_CATALOG.filter((i) => i.room === room).map((i) => (
              <option key={i.id} value={`catalog:${i.id}`}>
                {t(`catalog.items.${i.id}`)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <span id={`${id}-hint`} className="hint">
        {pick ? describeSpec(t, pick.spec, item) : t('catalog.hint')}
      </span>
    </div>
  )
}

/**
 * « Copier dans un autre foyer » : le modèle est recopié (nom et description) dans un autre foyer où la personne peut
 * modifier l'inventaire. Les deux copies vivent ensuite chacune de leur côté.
 */
const PERSONAL = 'personal'

function CopyTemplate({ householdId, template }: { householdId: string; template: FurnitureTemplate }) {
  const { t } = useTranslation()
  const id = useId()
  const qc = useQueryClient()
  const { households } = useHousehold()
  const targets = households.filter((h) => h.id !== householdId && h.role !== 'viewer' && !h.readOnly)
  const [target, setTarget] = useState('')
  // « Tous mes foyers » : copie en modèle personnel (proposé dans chacun de mes foyers, invisible des autres membres).
  const copy = useMutation({
    mutationFn: (to: string) =>
      to === PERSONAL
        ? api.post(`/households/${householdId}/furniture-templates`, { name: template.name, spec: template.spec, scope: 'personal' })
        : api.post(`/households/${to}/furniture-templates`, { name: template.name, spec: template.spec }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['furniture-templates'] }),
  })
  const done = !copy.isSuccess
    ? null
    : copy.variables === PERSONAL
      ? t('catalog.allMyHouseholds')
      : targets.find((h) => h.id === copy.variables)?.name
  return (
    <div className="row" style={{ gap: '0.5rem' }}>
      <label htmlFor={id} className="muted">
        {t('catalog.copyTemplate')}
      </label>
      <select id={id} value={target} onChange={(e) => setTarget(e.target.value)}>
        <option value="">{t('catalog.copyTemplatePick')}</option>
        <option value={PERSONAL}>{t('catalog.allMyHouseholds')}</option>
        {targets.map((h) => (
          <option key={h.id} value={h.id}>
            {h.name}
          </option>
        ))}
      </select>
      <button type="button" className="btn secondary" disabled={!target || copy.isPending} onClick={() => copy.mutate(target)}>
        {t('catalog.copyTemplateDo')}
      </button>
      {done && (
        <span role="status" className="muted">
          {t('catalog.templateCopied', { household: done })}
        </span>
      )}
      <ErrorAlert error={copy.error} />
    </div>
  )
}
